import { useEffect, useState } from 'react';

export interface VisualViewportState {
  /** Zichtbare hoogte in px (zonder schermtoetsenbord), of null als de browser geen visualViewport kent. */
  height: number | null;
  /** true als de gebruiker inzoomt (pinch-zoom): dan zegt de visualViewport-hoogte niets over het toetsenbord. */
  zoomed: boolean;
  /** Verschuiving van de zichtbare viewport t.o.v. de layout-viewport (iOS scrolt die bij een open toetsenbord). */
  offsetTop: number;
  keyboardOpen: boolean;
}

const KEYBOARD_THRESHOLD_PX = 150;
const IDLE: VisualViewportState = { height: null, offsetTop: 0, zoomed: false, keyboardOpen: false };

/**
 * Volgt de visualViewport zodat een volledig scherm-dialoog niet onder het schermtoetsenbord verdwijnt
 * (iOS Safari verkleint de layout-viewport niet; Android Chrome doet dat meestal wel). Alleen actief als `enabled`;
 * jsdom en oudere browsers kennen visualViewport niet en vallen terug op `IDLE`.
 */
export default function useVisualViewport(enabled: boolean): VisualViewportState {
  const [state, setState] = useState<VisualViewportState>(IDLE);

  useEffect(() => {
    const viewport = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!enabled || !viewport) {
      setState(IDLE);
      return undefined;
    }
    const update = () => {
      const zoomed = viewport.scale > 1.01;
      setState({
        height: Math.round(viewport.height),
        offsetTop: Math.round(viewport.offsetTop),
        zoomed,
        keyboardOpen: !zoomed && window.innerHeight - viewport.height > KEYBOARD_THRESHOLD_PX,
      });
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    window.addEventListener('orientationchange', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [enabled]);

  return state;
}
